// Storage, parsing and timing for personal-best ghosts. Kept free of Three.js
// so the menu can read and validate ghosts without loading the renderer.
import type {
  CrewModule,
  MapDefinition,
  RacePhase,
  RaceSettings,
  RaceSnapshot,
  RacerSnapshot,
  TimeOfDay,
  Weather,
} from '../../shared/types.js';
import { isWeather } from '../../shared/types.js';
import { normaliseArcadePreset } from '../../shared/arcade-presets.js';
import { normaliseCrewModule } from '../../shared/crew-modules.js';
import { raceEventMode } from '../../shared/race-events.js';
import { getRacingPlan } from '../../shared/racing-line.js';
import { clamp, signedAngleDifference } from '../../shared/track.js';
import { withOriginLock } from '../origin-lock.js';

export type TimeTrialMedal = 'gold' | 'silver' | 'bronze';

export interface TimeTrialTargets {
  goldMs: number;
  silverMs: number;
  bronzeMs: number;
}

export interface GhostStatus {
  active: boolean;
  recording: boolean;
  /** Fastest lap actually driven on this device. */
  bestLapMs: number | null;
  /** Lap currently rendered and used for live deltas. A shared challenge can
   * be the reference without impersonating the player's personal best. */
  referenceLapMs: number | null;
  challengeName: string | null;
  deltaMs: number | null;
  newBest: boolean;
  currentSector: number | null;
  sectorDeltaMs: number | null;
  bestSectorTimesMs: readonly number[];
  referenceSectorTimesMs: readonly number[];
  lastLapMs: number | null;
  medal: TimeTrialMedal | null;
  targets: TimeTrialTargets;
  persistence: 'idle' | 'pending' | 'saved' | 'failed';
}

export interface GhostSample {
  timeMs: number;
  progress: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface StoredLapGhost {
  version: 1;
  mapId: string;
  lapTimeMs: number;
  carId: string;
  crewModule: CrewModule;
  /** Exact environment/rules identity for new recordings. Missing values are
   * accepted only while decoding legacy records and are never auto-matched. */
  timeOfDay?: TimeOfDay;
  weather?: Weather;
  dailyChallengeId?: string;
  updatedAt: number;
  samples: GhostSample[];
  /** Sector split of the stored fastest lap. */
  sectorTimesMs?: [number, number, number];
  /** Fastest individual split from any completed local lap. */
  bestSectorTimesMs?: [number, number, number];
}

const STORAGE_PREFIX = 'nightshift:lap-ghost:v1:';
// v2 records predate canonical Time Trial eligibility and may have been
// produced by circuit races or traffic-assisted runs. Do not silently promote
// them into shareable personal bests.
const EXACT_STORAGE_PREFIX = 'nightshift:lap-ghost:v3:';
const TIME_OF_DAY = new Set<TimeOfDay>(['dawn', 'day', 'sunset', 'night']);
export const MAX_STORED_GHOST_SAMPLES = 2_048;
const MAX_LEGACY_GHOST_SAMPLES = 20_000;

export interface LapGhostSetup {
  carId: string;
  crewModule: CrewModule;
  timeOfDay: TimeOfDay;
  weather: Weather;
  dailyChallengeId?: string;
}

type LapGhostRaceRules = Pick<
  RaceSettings,
  'antiCheat' | 'arcadePreset' | 'collisions' | 'eventMode' | 'npcCount' | 'timeTrial' | 'traffic'
>;

export function lapGhostRecordingEligible(settings: LapGhostRaceRules): boolean {
  return raceEventMode(settings) === 'time-trial'
    && settings.npcCount === 0
    && settings.antiCheat !== true
    && settings.collisions === false
    && settings.traffic !== true
    && normaliseArcadePreset(settings.arcadePreset) === 'standard';
}

export interface LapGhostReferenceChallenge {
  ghost: StoredLapGhost;
  challenger: string;
}

/** A local-only holographic opponent. It never enters simulation or networking. */
export function sampleGhostAtTime(samples: GhostSample[], timeMs: number): GhostSample | null {
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last) return null;
  if (timeMs <= first.timeMs) return { ...first };
  if (timeMs >= last.timeMs) return { ...last };
  let lower = 1;
  let upper = samples.length - 1;
  while (lower < upper) {
    const middle = (lower + upper) >>> 1;
    if (samples[middle]!.timeMs < timeMs) lower = middle + 1;
    else upper = middle;
  }
  const next = samples[lower] ?? last;
  const previous = samples[lower - 1] ?? first;
  const alpha = clamp((timeMs - previous.timeMs) / Math.max(1, next.timeMs - previous.timeMs), 0, 1);
  return {
    timeMs,
    progress: mix(previous.progress, next.progress, alpha),
    x: mix(previous.x, next.x, alpha),
    y: mix(previous.y, next.y, alpha),
    z: mix(previous.z, next.z, alpha),
    yaw: previous.yaw + signedAngleDifference(next.yaw, previous.yaw) * alpha,
  };
}

export function parseLapGhost(raw: string | null, mapId: string): StoredLapGhost | null {
  if (!raw) return null;
  try {
    const candidate = JSON.parse(raw) as Partial<StoredLapGhost>;
    if (candidate.version !== 1 || candidate.mapId !== mapId || typeof candidate.carId !== 'string') return null;
    const lapTimeMs = Number(candidate.lapTimeMs);
    if (!Number.isFinite(lapTimeMs) || lapTimeMs < 5_000 || lapTimeMs > 15 * 60_000) return null;
    if (!Array.isArray(candidate.samples) || candidate.samples.length < 20 || candidate.samples.length > MAX_LEGACY_GHOST_SAMPLES) return null;
    const samples: GhostSample[] = [];
    let previousTime = Number.NEGATIVE_INFINITY;
    for (const sample of candidate.samples) {
      if (!sample || typeof sample !== 'object') return null;
      const values = [sample.timeMs, sample.progress, sample.x, sample.y, sample.z, sample.yaw];
      if (!values.every((value) => Number.isFinite(value))) return null;
      const timeMs = Number(sample.timeMs);
      const progress = Number(sample.progress);
      const x = Number(sample.x);
      const y = Number(sample.y);
      const z = Number(sample.z);
      if (
        timeMs < previousTime
        || timeMs < 0
        || timeMs > lapTimeMs + 1
        || progress < 0
        || progress > 1
        || Math.abs(x) > 10_000
        || Math.abs(y) > 2_000
        || Math.abs(z) > 10_000
      ) return null;
      previousTime = timeMs;
      samples.push({
        timeMs,
        progress,
        x,
        y,
        z,
        yaw: Number(sample.yaw),
      });
    }
    const first = samples[0]!;
    const last = samples[samples.length - 1]!;
    if (
      first.timeMs > Math.max(1_000, lapTimeMs * 0.05)
      || first.progress > 0.2
      || last.timeMs < lapTimeMs * 0.9
      || last.progress < 0.75
    ) return null;
    const sectorTimesMs = parseSectorTimes(candidate.sectorTimesMs);
    if (
      candidate.sectorTimesMs !== undefined
      && (!sectorTimesMs || Math.abs(sectorTimesMs.reduce((sum, time) => sum + time, 0) - lapTimeMs) > Math.max(250, lapTimeMs * 0.02))
    ) return null;
    const timeOfDay = candidate.timeOfDay;
    const weather = candidate.weather;
    if (
      (timeOfDay === undefined) !== (weather === undefined)
      || (timeOfDay !== undefined && !TIME_OF_DAY.has(timeOfDay))
      || (weather !== undefined && !isWeather(weather))
    ) return null;
    const dailyChallengeId = candidate.dailyChallengeId;
    if (
      dailyChallengeId !== undefined
      && (
        typeof dailyChallengeId !== 'string'
        || dailyChallengeId.length > 32
        || !/^[a-z0-9-]+$/i.test(dailyChallengeId)
      )
    ) return null;
    return {
      version: 1,
      mapId,
      lapTimeMs: Math.round(lapTimeMs),
      carId: candidate.carId.slice(0, 80),
      crewModule: normaliseCrewModule(candidate.crewModule),
      timeOfDay,
      weather,
      dailyChallengeId,
      updatedAt: Number.isFinite(candidate.updatedAt) ? Number(candidate.updatedAt) : 0,
      samples: compactGhostSamples(samples, MAX_STORED_GHOST_SAMPLES),
      sectorTimesMs,
      bestSectorTimesMs: parseSectorTimes(candidate.bestSectorTimesMs),
    };
  } catch {
    return null;
  }
}

export function timeTrialTargets(map: MapDefinition): TimeTrialTargets {
  const baselineMs = Math.max(20_000, getRacingPlan(map).estimatedLapTime * 1_000);
  const goldMs = roundToHundred(baselineMs * 1.14);
  const silverMs = Math.max(goldMs + 2_500, roundToHundred(baselineMs * 1.28));
  const bronzeMs = Math.max(silverMs + 3_000, roundToHundred(baselineMs * 1.46));
  return { goldMs, silverMs, bronzeMs };
}

export function medalForLap(lapTimeMs: number, targets: TimeTrialTargets): TimeTrialMedal | null {
  if (!Number.isFinite(lapTimeMs) || lapTimeMs <= 0) return null;
  if (lapTimeMs <= targets.goldMs) return 'gold';
  if (lapTimeMs <= targets.silverMs) return 'silver';
  if (lapTimeMs <= targets.bronzeMs) return 'bronze';
  return null;
}

function parseSectorTimes(value: unknown): [number, number, number] | undefined {
  if (!Array.isArray(value) || value.length !== 3) return undefined;
  const times = value.map(Number);
  if (!times.every((time) => Number.isFinite(time) && time > 0 && time < 15 * 60_000)) return undefined;
  return [Math.round(times[0]!), Math.round(times[1]!), Math.round(times[2]!)];
}

function roundToHundred(value: number): number {
  return Math.round(value / 100) * 100;
}

export function ghostTimeAtProgress(samples: GhostSample[], progress: number): number {
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last) return 0;
  if (progress <= first.progress) return first.timeMs;
  if (progress >= last.progress) return last.timeMs;
  let lower = 1;
  let upper = samples.length - 1;
  while (lower < upper) {
    const middle = (lower + upper) >>> 1;
    if (samples[middle]!.progress < progress) lower = middle + 1;
    else upper = middle;
  }
  const next = samples[lower] ?? last;
  const previous = samples[lower - 1] ?? first;
  return Math.abs(next.progress - progress) < Math.abs(previous.progress - progress)
    ? next.timeMs
    : previous.timeMs;
}

export function loadLapGhost(mapId: string, setup?: LapGhostSetup): StoredLapGhost | null {
  try {
    const key = setup ? lapGhostStorageKey(mapId, setup) : `${STORAGE_PREFIX}${mapId}`;
    const ghost = parseLapGhost(localStorage.getItem(key), mapId);
    return setup && ghost && !ghostMatchesRaceSetup(ghost, mapId, setup) ? null : ghost;
  } catch {
    return null;
  }
}

export function saveLapGhost(ghost: StoredLapGhost): StoredLapGhost {
  return saveLapGhostWithStatus(ghost).ghost;
}

export interface LapGhostPersistenceResult {
  ghost: StoredLapGhost;
  persisted: boolean;
}

export interface LapGhostSaveOperation {
  ghost: StoredLapGhost;
  settled: Promise<LapGhostPersistenceResult>;
}

export function saveLapGhostWithStatus(ghost: StoredLapGhost): LapGhostSaveOperation {
  const candidate = cloneLapGhost(ghost);
  const setup = exactSetupFromGhost(candidate);
  const key = setup
    ? lapGhostStorageKey(candidate.mapId, setup)
    : `${STORAGE_PREFIX}${candidate.mapId}`;
  const preview = previewLapGhostMerge(key, candidate);
  const lockId = stableStorageHash(key);
  const pending = withOriginLock(
    `nightshift:lap-ghost:${lockId}`,
    `nightshift:lap-ghost-lease:v1:${lockId}`,
    () => mergeAndWriteLapGhost(key, candidate),
  ).catch(() => ({ ghost: preview, persisted: false }));
  pendingLapGhostWrites.add(pending);
  void pending.finally(() => pendingLapGhostWrites.delete(pending));
  return { ghost: preview, settled: pending };
}

const pendingLapGhostWrites = new Set<Promise<LapGhostPersistenceResult>>();

/** Test/lifecycle hook for callers that must wait until cross-tab reconciliation
 * and the durable write have finished. */
export async function waitForLapGhostPersistence(): Promise<void> {
  while (pendingLapGhostWrites.size > 0) {
    await Promise.all([...pendingLapGhostWrites]);
  }
}

function previewLapGhostMerge(key: string, candidate: StoredLapGhost): StoredLapGhost {
  try {
    const current = parseLapGhost(localStorage.getItem(key), candidate.mapId);
    const matching = current && lapGhostKey(current) === key ? current : null;
    return mergeLapGhosts(matching, candidate);
  } catch {
    return candidate;
  }
}

function mergeAndWriteLapGhost(
  key: string,
  candidate: StoredLapGhost,
): LapGhostPersistenceResult {
  let merged = cloneLapGhost(candidate);
  try {
    const current = parseLapGhost(localStorage.getItem(key), candidate.mapId);
    const matching = current && lapGhostKey(current) === key ? current : null;
    merged = mergeLapGhosts(matching, candidate);
    localStorage.setItem(key, JSON.stringify(merged));
    return { ghost: merged, persisted: true };
  } catch {
    // A private browser can still record and race the in-memory ghost.
    return { ghost: merged, persisted: false };
  }
}

function mergeLapGhosts(
  current: StoredLapGhost | null,
  candidate: StoredLapGhost,
): StoredLapGhost {
  if (!current) return cloneLapGhost(candidate);
  const winner = candidate.lapTimeMs < current.lapTimeMs ? candidate : current;
  const currentSectors = current.bestSectorTimesMs ?? current.sectorTimesMs;
  const candidateSectors = candidate.bestSectorTimesMs ?? candidate.sectorTimesMs;
  const bestSectorTimesMs = currentSectors && candidateSectors
    ? currentSectors.map((value, index) => Math.min(value, candidateSectors[index]!)) as [number, number, number]
    : currentSectors ?? candidateSectors;
  return {
    ...cloneLapGhost(winner),
    ...(bestSectorTimesMs ? { bestSectorTimesMs: [...bestSectorTimesMs] as [number, number, number] } : {}),
  };
}

function cloneLapGhost(ghost: StoredLapGhost): StoredLapGhost {
  return {
    ...ghost,
    samples: ghost.samples.map((sample) => ({ ...sample })),
    ...(ghost.sectorTimesMs ? { sectorTimesMs: [...ghost.sectorTimesMs] as [number, number, number] } : {}),
    ...(ghost.bestSectorTimesMs
      ? { bestSectorTimesMs: [...ghost.bestSectorTimesMs] as [number, number, number] }
      : {}),
  };
}

function lapGhostKey(ghost: StoredLapGhost): string {
  const setup = exactSetupFromGhost(ghost);
  return setup ? lapGhostStorageKey(ghost.mapId, setup) : `${STORAGE_PREFIX}${ghost.mapId}`;
}

function stableStorageHash(value: string): string {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(36);
}

export function compactGhostSamples(samples: readonly GhostSample[], maximum: number): GhostSample[] {
  const limit = Math.max(2, Math.floor(maximum));
  if (samples.length <= limit) return samples.map((sample) => ({ ...sample }));
  return Array.from({ length: limit }, (_value, index) => {
    const sourceIndex = Math.round(index / (limit - 1) * (samples.length - 1));
    return { ...samples[sourceIndex]! };
  });
}

export function buildGhostProgressLookup(samples: readonly GhostSample[]): GhostSample[] {
  return [...samples].sort((first, second) => (
    first.progress - second.progress || first.timeMs - second.timeMs
  ));
}

export function cleanGhostChallenger(value: unknown): string {
  if (typeof value !== 'string') return 'CHALLENGER';
  return value.replace(/[<>\u0000-\u001f]/g, '').trim().slice(0, 18) || 'CHALLENGER';
}

export function ghostMatchesVehicleSetup(
  ghost: Pick<StoredLapGhost, 'carId' | 'crewModule'>,
  carId: string,
  crewModule: unknown,
): boolean {
  return ghost.carId === carId && ghost.crewModule === normaliseCrewModule(crewModule);
}

export function ghostMatchesRaceSetup(
  ghost: StoredLapGhost,
  mapId: string,
  setup: LapGhostSetup,
): boolean {
  return ghost.mapId === mapId
    && ghostMatchesVehicleSetup(ghost, setup.carId, setup.crewModule)
    && ghost.timeOfDay === setup.timeOfDay
    && ghost.weather === setup.weather
    && (ghost.dailyChallengeId ?? undefined) === (setup.dailyChallengeId ?? undefined);
}

export function lapGhostStorageKey(mapId: string, setup: LapGhostSetup): string {
  const daily = setup.dailyChallengeId ?? 'standard';
  return `${EXACT_STORAGE_PREFIX}${mapId}:${setup.carId}:${normaliseCrewModule(setup.crewModule)}:${setup.timeOfDay}:${setup.weather}:${daily}`;
}

function exactSetupFromGhost(ghost: StoredLapGhost): LapGhostSetup | null {
  if (!ghost.timeOfDay || !ghost.weather) return null;
  return {
    carId: ghost.carId,
    crewModule: ghost.crewModule,
    timeOfDay: ghost.timeOfDay,
    weather: ghost.weather,
    dailyChallengeId: ghost.dailyChallengeId,
  };
}

function mix(from: number, to: number, alpha: number): number {
  return from + (to - from) * alpha;
}

export function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}
